import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import {
  Blog,
  SocialPost,
  Corner,
  getBlog,
  getSettings,
  approveBlog,
  applyLogoToBlog,
  rejectBlog,
  regenerateBlog,
  publishBlog,
  fixFlags,
} from "../lib/api";
import ReviewFlagBanner from "../components/ReviewFlagBanner";
import SocialPostCard from "../components/SocialPostCard";
import LogoCornerPicker from "../components/LogoCornerPicker";
import Button from "../ui/Button";
import ErrorBoundary from "../ui/ErrorBoundary";
import PipelineProgress from "../ui/PipelineProgress";
import { TabsBar } from "../ui/Tabs";
import { useAsyncAction } from "../ui/useAsyncAction";
import {
  IconChevronLeft,
  IconExternalLink,
  IconRefresh,
  IconSend,
  IconSparkles,
  IconX,
} from "../ui/icons";

type Tab = "content" | "image" | "social";

export default function BlogDetail() {
  const { id } = useParams<{ id: string }>();
  const [blog, setBlog] = useState<Blog | null>(null);
  const [social, setSocial] = useState<SocialPost[]>([]);
  const [brandLogoUrl, setBrandLogoUrl] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("content");
  const [err, setErr] = useState<string | null>(null);
  const [feedback, setFeedback] = useState("");
  const [regenStage, setRegenStage] =
    useState<"write" | "image" | "keyword" | null>(null);

  const load = async () => {
    if (!id) return;
    try {
      const { blog, social } = await getBlog(id);
      setBlog(blog);
      setSocial(social);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  useEffect(() => {
    load();
    getSettings()
      .then((s) => setBrandLogoUrl(s.brand_logo_url))
      .catch(() => {});
    const t = setInterval(load, 8_000);
    return () => clearInterval(t);
  }, [id]);

  const approve = useAsyncAction(
    () => (blog ? approveBlog(blog.id) : Promise.reject(new Error("No blog"))),
    { successMessage: "Approved", onSuccess: load }
  );
  const reject = useAsyncAction(
    () => (blog ? rejectBlog(blog.id) : Promise.reject(new Error("No blog"))),
    { successMessage: "Rejected", onSuccess: load }
  );
  const publish = useAsyncAction(
    () => (blog ? publishBlog(blog.id) : Promise.reject(new Error("No blog"))),
    {
      loadingMessage: "Publishing to WordPress…",
      successMessage: "Published",
      successDescription: "Your blog is now live on the site.",
      onSuccess: load,
    }
  );
  const fixFlaggedIssues = useAsyncAction(
    () => (blog ? fixFlags(blog.id) : Promise.reject(new Error("No blog"))),
    {
      successMessage: "Regenerating with flag feedback",
      successDescription: "Watch the progress bar — it'll re-draft and re-review.",
      onSuccess: load,
    }
  );
  const applyLogo = useAsyncAction(
    (corner: Corner | null) =>
      blog
        ? applyLogoToBlog(blog.id, corner)
        : Promise.reject(new Error("No blog")),
    {
      successMessage: "Applying logo",
      successDescription: "The composite will appear in a few seconds.",
      onSuccess: () => setTimeout(load, 2500),
    }
  );
  const doRegenerate = useAsyncAction(
    async () => {
      if (!blog || !regenStage) throw new Error("No stage selected");
      await regenerateBlog(blog.id, regenStage, feedback || undefined);
    },
    {
      successMessage: "Regenerating",
      successDescription: "Watch the progress bar for updates.",
      onSuccess: () => {
        setRegenStage(null);
        setFeedback("");
        load();
      },
    }
  );

  if (!blog) return <div className="text-ink-500">Loading…</div>;

  return (
    <div>
      <Link
        to="/"
        className="inline-flex items-center gap-1 text-sm text-ink-500 hover:text-ink-800"
      >
        <IconChevronLeft size={14} />
        Back to dashboard
      </Link>

      <div className="mt-4 mb-6">
        <h1 className="font-display text-3xl md:text-4xl font-bold text-ink-800 tracking-tight leading-tight">
          {blog.title ?? blog.chosen_topic ?? "(untitled)"}
        </h1>
        <div className="mt-2 flex items-center gap-3 text-sm text-ink-500 flex-wrap">
          <span>Created {new Date(blog.created_at).toLocaleDateString()}</span>
          {blog.triggered_by === "weekly_cron" && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-ink-100 text-ink-600 text-xs font-medium">
              Auto
            </span>
          )}
          {blog.wordpress_url && (
            <a
              href={blog.wordpress_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-brand-600 hover:text-brand-700 font-medium"
            >
              View on site
              <IconExternalLink size={12} />
            </a>
          )}
        </div>
      </div>

      <div className="mb-8">
        <PipelineProgress
          kind="blog"
          status={blog.status}
          reviewPassed={blog.review_passed}
          errorMessage={blog.error_message}
          variant="full"
          action={
            <ContextualAction
              blog={blog}
              onApprove={() => approve.run()}
              approvePending={approve.pending}
              onReject={() => reject.run()}
              rejectPending={reject.pending}
              onPublish={() => publish.run()}
              publishPending={publish.pending}
              onFix={() => fixFlaggedIssues.run()}
              fixPending={fixFlaggedIssues.pending}
            />
          }
        />
      </div>

      {err && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {err}
        </div>
      )}

      <div className="mb-6">
        <ReviewFlagBanner flags={blog.review_flags} passed={blog.review_passed} />
      </div>

      {blog.kit_broadcast_id && (
        <div className="mb-6">
          <KitBroadcastCard blog={blog} />
        </div>
      )}

      <TabsBar
        tabs={[
          { key: "content", label: "Blog content" },
          { key: "image", label: "Infographic" },
          {
            key: "social",
            label: "Social posts",
            badge: (
              <span className="ml-1 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-ink-100 text-ink-700 text-[11px] font-medium tabular-nums">
                {social.length}
              </span>
            ),
          },
        ]}
        active={tab}
        onChange={(k) => setTab(k as Tab)}
        className="mb-6"
      />

      {tab === "content" && (
        <section>
          <article className="bg-white border border-ink-100 rounded-xl p-6 md:p-8 shadow-sm shadow-ink-900/5">
            {blog.body_markdown ? (
              <div className="prose prose-lg max-w-none prose-headings:tracking-tight prose-blockquote:border-brand-500 prose-blockquote:not-italic prose-strong:text-ink-900">
                <ReactMarkdown>{blog.body_markdown}</ReactMarkdown>
              </div>
            ) : (
              <p className="text-ink-500">Pipeline still running…</p>
            )}
          </article>
          {blog.target_keywords.length > 0 && (
            <div className="mt-5">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-500 mb-2">
                Target keywords
              </h3>
              <div className="flex gap-1.5 flex-wrap">
                {blog.target_keywords.map((k) => (
                  <span
                    key={k}
                    className="text-xs px-2 py-1 rounded-md bg-brand-50 text-brand-800 border border-brand-100"
                  >
                    {k}
                  </span>
                ))}
              </div>
            </div>
          )}
          <div className="mt-5 flex gap-2 flex-wrap">
            <Button
              variant="secondary"
              onClick={() => setRegenStage("write")}
              iconLeft={<IconRefresh size={14} />}
            >
              Regenerate body
            </Button>
            <Button
              variant="secondary"
              onClick={() => setRegenStage("keyword")}
              iconLeft={<IconRefresh size={14} />}
            >
              Re-run keyword pass
            </Button>
          </div>
        </section>
      )}

      {tab === "image" && (
        <section>
          {blog.feature_image_url ? (
            <img
              src={blog.feature_image_url}
              alt=""
              className="rounded-xl max-h-[500px] object-cover border border-ink-100 bg-white"
            />
          ) : (
            <div className="rounded-xl border border-dashed border-ink-200 p-12 text-center text-ink-500 bg-white">
              Image not yet generated.
            </div>
          )}
          <div className="mt-5 flex flex-wrap items-start gap-3">
            <Button
              variant="secondary"
              onClick={() => setRegenStage("image")}
              iconLeft={<IconRefresh size={14} />}
            >
              Regenerate image
            </Button>
            {blog.feature_image_url && (
              <LogoCornerPicker
                currentCorner={blog.feature_image_logo_corner}
                hasLogo={!!brandLogoUrl}
                onApply={async (corner: Corner | null) => {
                  await applyLogo.run(corner);
                }}
              />
            )}
          </div>
          {blog.feature_image_prompt && (
            <div className="mt-5">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-500 mb-1.5">
                Prompt used
              </h3>
              <p className="text-sm text-ink-600 whitespace-pre-wrap bg-ink-50 border border-ink-100 rounded-lg p-3">
                {blog.feature_image_prompt}
              </p>
            </div>
          )}
        </section>
      )}

      {tab === "social" && (
        <ErrorBoundary label="the social posts tab">
        <section className="grid gap-4">
          {social.length === 0 ? (
            <div className="bg-white border border-ink-100 rounded-xl p-10 text-center">
              {blog.status === "published" ? (
                <>
                  <p className="text-ink-800 font-medium">
                    Social posts are being generated…
                  </p>
                  <p className="text-ink-500 text-sm mt-1">
                    Each post runs through the review agent before appearing
                    here.
                  </p>
                </>
              ) : (
                <>
                  <p className="text-ink-800 font-medium">
                    Social posts appear after the blog is published.
                  </p>
                  <p className="text-ink-500 text-sm mt-1">
                    Approve the blog, then publish to WordPress. The social
                    pipeline runs automatically from there.
                  </p>
                </>
              )}
            </div>
          ) : (
            social.map((p) => (
              <ErrorBoundary key={p.id} label={`social post #${(p.post_index ?? 0) + 1}`}>
                <SocialPostCard
                  post={p}
                  blog={blog}
                  hasLogo={!!brandLogoUrl}
                  onChanged={load}
                />
              </ErrorBoundary>
            ))
          )}
        </section>
        </ErrorBoundary>
      )}

      {regenStage && (
        <RegenModal
          stage={regenStage}
          feedback={feedback}
          setFeedback={setFeedback}
          pending={doRegenerate.pending}
          onSubmit={() => doRegenerate.run()}
          onClose={() => {
            setRegenStage(null);
            setFeedback("");
          }}
        />
      )}
    </div>
  );
}

function KitBroadcastCard({ blog }: { blog: Blog }) {
  const isDraft = blog.kit_broadcast_status === "draft" || !blog.kit_sent_at;
  const stat = (label: string, value: string) => (
    <div className="rounded-lg border border-ink-100 bg-sand-50 px-3 py-2">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-500">
        {label}
      </p>
      <p className="font-display text-xl font-bold text-ink-800 tabular-nums mt-0.5">
        {value}
      </p>
    </div>
  );

  return (
    <section className="bg-white border border-ink-100 rounded-xl p-5 shadow-sm shadow-ink-900/5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-display text-base font-semibold text-ink-800">
          Email broadcast
        </h3>
        <span className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-md border border-brand-100 bg-brand-50 text-brand-800">
          <span className="h-2 w-2 rounded-full bg-brand-500" />
          {blog.kit_broadcast_status ?? "created"}
        </span>
      </div>

      {isDraft ? (
        <p className="text-sm text-ink-600 mt-2">
          A draft broadcast has been created in Kit. Open your Kit dashboard to
          preview the formatting, send yourself a test, and send it to the
          list when ready.
        </p>
      ) : (
        <>
          <p className="text-sm text-ink-500 mt-1">
            Sent to your Kit list
            {blog.kit_sent_at
              ? ` on ${new Date(blog.kit_sent_at).toLocaleDateString()}`
              : ""}
            .
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
            {stat("Recipients", String(blog.kit_recipients ?? "—"))}
            {stat(
              "Open rate",
              blog.kit_open_rate != null ? `${blog.kit_open_rate}%` : "—"
            )}
            {stat(
              "Click rate",
              blog.kit_click_rate != null ? `${blog.kit_click_rate}%` : "—"
            )}
            {stat("Clicks", String(blog.kit_total_clicks ?? "—"))}
          </div>
          {blog.kit_stats_updated_at ? (
            <p className="text-xs text-ink-400 mt-2">
              Analytics synced from Kit{" "}
              {new Date(blog.kit_stats_updated_at).toLocaleString()}.
            </p>
          ) : (
            <p className="text-xs text-ink-400 mt-2">
              Analytics sync from Kit once the broadcast has sent.
            </p>
          )}
        </>
      )}
    </section>
  );
}

function ContextualAction({
  blog,
  onApprove,
  approvePending,
  onReject,
  rejectPending,
  onPublish,
  publishPending,
  onFix,
  fixPending,
}: {
  blog: Blog;
  onApprove: () => void;
  approvePending: boolean;
  onReject: () => void;
  rejectPending: boolean;
  onPublish: () => void;
  publishPending: boolean;
  onFix: () => void;
  fixPending: boolean;
}) {
  if (blog.status === "awaiting_approval") {
    const reviewFailed = blog.review_passed === false;
    return (
      <div className="flex gap-2 flex-wrap">
        {reviewFailed && (
          <Button
            variant="primary"
            onClick={onFix}
            pending={fixPending}
            iconLeft={<IconSparkles size={14} />}
          >
            {fixPending ? "Regenerating…" : "Fix flagged issues"}
          </Button>
        )}
        <Button
          variant={reviewFailed ? "secondary" : "primary"}
          onClick={onApprove}
          pending={approvePending}
        >
          {approvePending
            ? "Approving…"
            : reviewFailed
              ? "Approve anyway"
              : "Approve"}
        </Button>
        <Button variant="ghost" onClick={onReject} pending={rejectPending}>
          {rejectPending ? "Rejecting…" : "Reject"}
        </Button>
      </div>
    );
  }
  if (blog.status === "approved") {
    return (
      <Button
        variant="primary"
        onClick={onPublish}
        pending={publishPending}
        iconLeft={<IconSend size={14} />}
      >
        {publishPending ? "Publishing…" : "Publish to WordPress"}
      </Button>
    );
  }
  return null;
}

function RegenModal({
  stage,
  feedback,
  setFeedback,
  pending,
  onSubmit,
  onClose,
}: {
  stage: "write" | "image" | "keyword";
  feedback: string;
  setFeedback: (v: string) => void;
  pending: boolean;
  onSubmit: () => void;
  onClose: () => void;
}) {
  const stageLabel =
    stage === "write" ? "body" : stage === "image" ? "image" : "keyword pass";
  return (
    <div
      className="fixed inset-0 z-40 bg-ink-950/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl w-full max-w-md shadow-xl shadow-ink-900/20"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-ink-100 flex items-start justify-between">
          <div>
            <h3 className="font-display text-lg font-semibold text-ink-800">
              Regenerate {stageLabel}
            </h3>
            <p className="text-xs text-ink-500 mt-0.5">
              Add optional guidance to steer the regeneration.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-ink-400 hover:text-ink-700 p-1 rounded-md"
          >
            <IconX size={16} />
          </button>
        </div>
        <div className="p-5">
          <textarea
            placeholder="e.g. make the tone warmer, lean on peer-reviewed sources, avoid mentioning X…"
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            rows={5}
            className="w-full p-3 border border-ink-200 rounded-md text-sm focus:border-brand-500 resize-none"
            autoFocus
          />
        </div>
        <div className="px-5 py-3 border-t border-ink-100 flex gap-2 justify-end bg-sand-50 rounded-b-xl">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            pending={pending}
            onClick={onSubmit}
            iconLeft={<IconRefresh size={14} />}
          >
            {pending ? "Queuing…" : "Regenerate"}
          </Button>
        </div>
      </div>
    </div>
  );
}
